import * as React from 'react';
import {
  ChevronLeft,
  Facebook,
  Instagram,
  Play,
  Plus,
  Share2,
  Sparkles,
  UploadCloud,
  Video,
  X,
  Youtube,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDebounce } from 'core/hooks/useDebounce';
import { showGlobalModal } from 'core/components/GlobalModal';
import { PaginatedCardGrid } from 'core/components/PaginatedCardGrid';
import { SetupLoader } from 'core/components/SetupComponents/SetupLoader';
import { useAuth } from 'core/context/AuthContext/AuthContextProvider';
import { apiFileUpload } from 'core/utils/fileUpload/api';
import httpclient from 'libs/Axios';
import imageBg from 'assets/images/imageBG.webp';
import { dmTextGenerationApi } from 'features/DigitalMarketing/api/dmTextGenerationApi';
import { DigitalMarketingBreadcrumb } from 'features/DigitalMarketing/components/DigitalMarketingBreadcrumb';
import { DigitalMarketingSearchInput } from 'features/DigitalMarketing/components/DigitalMarketingSearchInput';
import { Modal } from 'features/DigitalMarketing/components/ui/Modal';
import { Toggle } from 'features/DigitalMarketing/components/ui/Toggle';
import {
  editWebsiteContentInputClassName,
  editWebsiteContentLabelClassName,
  maxDescriptionGenerations,
} from 'features/DigitalMarketing/constants/formStyles';
import {
  buildUploadVideosPath,
  UPLOAD_VIDEOS_BASE_PATH,
} from 'features/DigitalMarketing/routes/uploadVideoPaths';
import {
  extractUploadedUrl,
  getApiErrorMessage,
  getResponseList,
} from 'features/DigitalMarketing/utils/apiResponse';
import {
  DMButton,
  DMDiv,
  DMImage,
  DMInput,
  DMLabel,
  DMText,
  DMTextarea,
  DMVideo,
} from '../../components/Primitives';

type VideoItem = {
  id: number;
  title: string;
  description: string;
  content: string;
  videoUrl: string;
  backgroundMusicUrl: string;
  presentationStyle: string;
  instagram: boolean;
  facebook: boolean;
  youtube: boolean;
  availablePlatforms: VideoPlatformKey[];
  createdAt: string;
  updatedAt: string;
  date: string;
  time: string;
  school: string;
  thumbnail: string;
  raw: any;
};

type SocialPostPayload = {
  MEDIA_TYPE: 'PHOTO' | 'VIDEO' | 'REEL';
  TITLE: string;
  DESCRIPTION?: string;
  MEDIA: string[];
  BRANCH_ID?: number | string;
};

type VideoFormValues = {
  title: string;
  description: string;
  content: string;
  videoUrl: string;
  videoFileName: string;
  backgroundMusicUrl: string;
  backgroundMusicFileName: string;
  presentationStyle: string;
  platform: string[];
};

const emptyVideoForm: VideoFormValues = {
  title: '',
  description: '',
  content: '',
  videoUrl: '',
  videoFileName: '',
  backgroundMusicUrl: '',
  backgroundMusicFileName: '',
  presentationStyle: 'Educational',
  platform: [],
};

const labelClassName = editWebsiteContentLabelClassName;
const inputClassName = editWebsiteContentInputClassName;
const allowedVideoTypes = ['video/mp4', 'video/webm', 'video/quicktime'];
const maxVideoSize = 100 * 1024 * 1024;

const emptyPlatforms = {
  facebook: false,
  instagram: false,
  youtube: false,
};

type VideoPlatformKey = keyof typeof emptyPlatforms;

const platformKeys = Object.keys(emptyPlatforms) as VideoPlatformKey[];

const normalizePlatformArray = (platform: unknown): string[] =>
  Array.isArray(platform)
    ? platform
        .filter((item): item is string => typeof item === 'string')
        .map(item => item.toLowerCase())
    : [];

const hasResponseKey = (item: any, key: string) =>
  Object.prototype.hasOwnProperty.call(item || {}, key);

const getVideoList = (response: any) =>
  response?.data?.data?.Videos ||
  response?.data?.data?.VIDEOS ||
  response?.data?.data?.videos ||
  getResponseList(response);

const getVideoTotal = (response: any) =>
  Number(
    response?.data?.data?.Total ||
      response?.data?.data?.TOTAL ||
      response?.data?.data?.total ||
      getVideoList(response).length
  );

const formatVideoDateTime = (dateValue: string) => {
  if (!dateValue) {
    return { date: '-', time: '-' };
  }

  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) {
    return { date: dateValue, time: '-' };
  }

  return {
    date: parsed.toLocaleDateString('en-GB'),
    time: parsed.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    }),
  };
};

const mapVideoRow = (item: any): VideoItem => {
  const createdAt =
    item?.CREATED_AT || item?.created_at || item?.createdAt || '';
  const updatedAt =
    item?.UPDATED_AT || item?.updated_at || item?.updatedAt || '';
  const displayDateTime = updatedAt || createdAt;
  const dateTime = formatVideoDateTime(displayDateTime);
  const legacyPlatforms = normalizePlatformArray(
    item?.platform || item?.PLATFORM
  );
  const facebook = Boolean(
    item?.facebook ?? item?.FACEBOOK ?? legacyPlatforms.includes('facebook')
  );
  const instagram = Boolean(
    item?.instagram ?? item?.INSTAGRAM ?? legacyPlatforms.includes('instagram')
  );
  const youtube = Boolean(
    item?.youtube ?? item?.YOUTUBE ?? legacyPlatforms.includes('youtube')
  );
  const hasBooleanPlatformKeys = platformKeys.some(
    key => hasResponseKey(item, key) || hasResponseKey(item, key.toUpperCase())
  );

  return {
    id: Number(item?.video_id || item?.VIDEO_ID || item?.id || item?.ID || 0),
    title: item?.title || item?.TITLE || 'Untitled Video',
    description: item?.description || item?.DESCRIPTION || '',
    content: item?.content || item?.CONTENT || '',
    videoUrl: item?.video_url || item?.VIDEO_URL || '',
    backgroundMusicUrl:
      item?.background_music_url || item?.BACKGROUND_MUSIC_URL || '',
    presentationStyle:
      item?.presentation_style || item?.PRESENTATION_STYLE || '',
    instagram,
    facebook,
    youtube,
    availablePlatforms: hasBooleanPlatformKeys
      ? [
          ...(hasResponseKey(item, 'facebook') ||
          hasResponseKey(item, 'FACEBOOK')
            ? ['facebook' as const]
            : []),
          ...(hasResponseKey(item, 'instagram') ||
          hasResponseKey(item, 'INSTAGRAM')
            ? ['instagram' as const]
            : []),
          ...(hasResponseKey(item, 'youtube') || hasResponseKey(item, 'YOUTUBE')
            ? ['youtube' as const]
            : []),
        ]
      : ['facebook', 'instagram', 'youtube'],
    createdAt,
    updatedAt,
    date: dateTime.date,
    time: dateTime.time,
    school: 'DemoSchool',
    thumbnail: imageBg,
    raw: item,
  };
};

const openSocialPostUrl = (response: any) => {
  const url = response?.data?.data?.url;

  if (typeof url === 'string' && url.trim()) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

const buildFacebookVideoPayload = (video: VideoItem): SocialPostPayload => {
  const raw = video.raw || {};
  const title = raw?.title || raw?.TITLE || video.title;
  const description =
    raw?.description || raw?.DESCRIPTION || video.description || video.content;
  const videoUrl = raw?.video_url || raw?.VIDEO_URL || video.videoUrl;
  const media = typeof videoUrl === 'string' && videoUrl ? [videoUrl] : [];

  return {
    MEDIA_TYPE: 'VIDEO',
    TITLE: title,
    DESCRIPTION: description,
    MEDIA: media.length ? media : video.videoUrl ? [video.videoUrl] : [],
  };
};

const VideoCard = ({
  item,
  onPlay,
  onShare,
}: {
  item: VideoItem;
  onPlay: (item: VideoItem) => void;
  onShare: (item: VideoItem) => void;
}) => (
  <DMDiv as="article" className="relative min-w-0">
    <DMButton
      type="button"
      onClick={() => onPlay(item)}
      className="relative block aspect-[1.72] w-full overflow-hidden rounded-[8px] bg-gray-200 text-left"
      aria-label={`Open ${item.title}`}
    >
      {item.videoUrl && isImageUrl(item.videoUrl) ? (
        <DMImage
          src={item.videoUrl}
          alt={item.title}
          className="h-full w-full object-cover"
        />
      ) : item.videoUrl ? (
        <DMVideo
          src={`${item.videoUrl}#t=0.1`}
          muted
          preload="metadata"
          playsInline
          className="h-full w-full object-cover"
        />
      ) : (
        <DMImage
          src={item.thumbnail}
          alt={item.title}
          className="h-full w-full object-cover"
        />
      )}
      {!isImageUrl(item.videoUrl) && (
        <DMDiv className="absolute inset-0 flex items-center justify-center">
          <DMText
            as="span"
            className="flex h-[7.8vh] w-[7.8vh] items-center justify-center rounded-full bg-black/70 text-white"
          >
            <Play
              className="ml-[0.3vw] h-[4.2vh] w-[4.2vh] fill-white"
              strokeWidth={1.6}
            />
          </DMText>
        </DMDiv>
      )}
    </DMButton>

    <DMDiv className="mt-[2vh] flex items-start justify-between gap-[1vw] pr-[2.6vw]">
      <DMDiv className="min-w-0 flex-1">
        <DMText
          as="h2"
          className="truncate text-[2.25vh] font-bold leading-tight text-gray-950"
        >
          {item.title}
        </DMText>
        <DMText className="mt-[1.2vh] truncate text-[1.55vh] font-medium leading-tight text-gray-500">
          {item.date} | {item.time} | {item.school}
        </DMText>
      </DMDiv>
    </DMDiv>

    <DMButton
      type="button"
      onClick={event => {
        event.stopPropagation();
        onShare(item);
      }}
      className="absolute bottom-[1.4vh] right-0 z-10 flex h-[3.6vh] w-[3.6vh] items-center justify-center rounded-full text-gray-950 hover:bg-gray-100"
      aria-label={`Share or publish ${item.title}`}
    >
      <Share2 className="h-[2.2vh] w-[2.2vh]" strokeWidth={2.3} />
    </DMButton>
  </DMDiv>
);

const VideoCardSkeleton = () => (
  <DMDiv className="min-w-0 animate-pulse" aria-hidden="true">
    <DMDiv className="aspect-[1.72] rounded-[8px] bg-gray-200" />
    <DMDiv className="mt-[2vh] flex items-start justify-between gap-[1vw]">
      <DMDiv className="min-w-0 flex-1">
        <DMDiv className="h-[2.4vh] w-[75%] rounded bg-gray-200" />
        <DMDiv className="mt-[1.2vh] h-[1.6vh] w-[58%] rounded bg-gray-200" />
      </DMDiv>
    </DMDiv>
  </DMDiv>
);

const DisabledVideoToggle = ({ checked }: { checked: boolean }) => (
  <DMButton
    type="button"
    disabled
    className={`w-[4.4vh] h-[2.4vh] rounded-[2vh] p-[0.3vh] transition-colors duration-200 ease-in-out cursor-not-allowed opacity-60 ${
      checked ? 'bg-primary-main' : 'bg-gray-200'
    }`}
  >
    <DMDiv
      className={`w-[1.8vh] h-[1.8vh] bg-white rounded-full shadow-sm transform transition-transform duration-200 ease-in-out ${
        checked ? 'translate-x-[2vh]' : 'translate-x-0'
      }`}
    />
  </DMButton>
);

const videoPlatformLabels: Record<VideoPlatformKey, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  youtube: 'YouTube',
};

const VideoPlatformIcon = ({ platform }: { platform: VideoPlatformKey }) => {
  if (platform === 'facebook') {
    return (
      <DMDiv className="flex h-8 w-8 items-center justify-center rounded bg-[#1877F2] text-white">
        <Facebook size={18} />
      </DMDiv>
    );
  }

  if (platform === 'instagram') {
    return (
      <DMDiv className="flex h-8 w-8 items-center justify-center rounded bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-500 text-white">
        <Instagram size={18} />
      </DMDiv>
    );
  }

  return (
    <DMDiv className="flex h-8 w-8 items-center justify-center rounded bg-[#FF0000] text-white">
      <Youtube size={18} />
    </DMDiv>
  );
};

const VideoPublishModal = ({
  isOpen,
  onClose,
  platforms,
  visiblePlatforms,
  requestPublish,
  isPublishing,
  publishingPlatform = null,
}: {
  isOpen: boolean;
  onClose: () => void;
  platforms: typeof emptyPlatforms;
  visiblePlatforms: VideoPlatformKey[];
  requestPublish: (key: VideoPlatformKey) => void;
  isPublishing: boolean;
  publishingPlatform?: VideoPlatformKey | null;
}) => (
  <Modal isOpen={isOpen} onClose={isPublishing ? () => undefined : onClose}>
    <DMDiv className="text-left">
      <DMText as="h2" className="mb-2 text-2xl font-bold text-gray-900">
        Publish Video
      </DMText>
      <DMText className="mb-8 text-gray-500">
        Select the social media platforms where you want to publish your video
      </DMText>

      <DMDiv className="space-y-4">
        {visiblePlatforms.map((platform, index) => (
          <DMDiv
            key={platform}
            className={`flex items-center justify-between ${
              index === visiblePlatforms.length - 1
                ? 'pb-4'
                : 'border-b border-gray-100 pb-4'
            }`}
          >
            <DMDiv className="flex items-center gap-3">
              <VideoPlatformIcon platform={platform} />
              <DMText as="span" className="font-medium text-gray-900">
                Share on {videoPlatformLabels[platform]}
              </DMText>
            </DMDiv>
            {platforms[platform] || isPublishing ? (
              <DMDiv className="flex items-center gap-3">
                {publishingPlatform === platform && (
                  <DMText
                    as="span"
                    className="text-sm font-medium text-primary-main"
                  >
                    Posting...
                  </DMText>
                )}
                <DisabledVideoToggle checked={platforms[platform]} />
              </DMDiv>
            ) : (
              <Toggle
                checked={platforms[platform]}
                onChange={() => requestPublish(platform)}
              />
            )}
          </DMDiv>
        ))}
      </DMDiv>

      {isPublishing && (
        <DMDiv className="mt-6 flex items-center gap-3 rounded-lg bg-primary-main/10 px-4 py-3 text-sm font-medium text-primary-main">
          <DMText
            as="span"
            className="h-4 w-4 animate-spin rounded-full border-2 border-primary-main/30 border-t-primary-main"
          />
          <DMText as="span">
            Publishing video
            {publishingPlatform
              ? ` on ${videoPlatformLabels[publishingPlatform]}`
              : ''}
            . Please wait...
          </DMText>
        </DMDiv>
      )}
    </DMDiv>
  </Modal>
);

const VideoPlayerModal = ({
  item,
  isLoading,
  onClose,
}: {
  item: VideoItem | null;
  isLoading: boolean;
  onClose: () => void;
}) => {
  if (!item && !isLoading) {
    return null;
  }

  return (
    <DMDiv className="fixed inset-0 z-[10002] flex items-center justify-center px-[3vw] py-[4vh]">
      <DMButton
        type="button"
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
        aria-label="Close video player backdrop"
      />
      <DMDiv className="relative z-10 flex max-h-[88vh] w-[65vw] max-w-[980px] flex-col overflow-hidden rounded-[10px] bg-white shadow-[0_18px_45px_rgba(0,0,0,0.35)]">
        <DMDiv className="flex shrink-0 items-center justify-between gap-[2vw] border-b border-gray-200 px-[1.4vw] py-[1.8vh]">
          <DMText
            as="h2"
            className="min-w-0 truncate text-[2.4vh] font-bold leading-tight text-gray-950"
          >
            {item?.title || 'Video'}
          </DMText>
          <DMButton
            type="button"
            onClick={onClose}
            className="flex h-[4vh] w-[4vh] shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200"
            aria-label="Close video player"
          >
            <X className="h-[2.4vh] w-[2.4vh]" strokeWidth={2.3} />
          </DMButton>
        </DMDiv>

        <DMDiv className="min-h-0 overflow-y-auto">
          <DMDiv className="flex justify-center bg-gray-100 px-[1.4vw] py-[1.8vh]">
            <DMDiv className="relative aspect-[2.36] max-h-[58vh] w-full max-w-[58vw] overflow-hidden rounded-[8px] bg-gray-900">
              {isLoading ? (
                <DMDiv className="flex h-full items-center justify-center bg-black">
                  <SetupLoader height="h-8" />
                </DMDiv>
              ) : item?.videoUrl && isImageUrl(item.videoUrl) ? (
                <DMImage
                  src={item.videoUrl}
                  alt={item.title}
                  className="h-full w-full object-cover"
                />
              ) : item?.videoUrl ? (
                <DMVideo
                  src={item.videoUrl}
                  poster={item.thumbnail}
                  controls
                  className="h-full w-full object-cover"
                />
              ) : (
                <>
                  <DMImage
                    src={item?.thumbnail || imageBg}
                    alt={item?.title || 'Video'}
                    className="h-full w-full object-cover"
                  />
                  <DMDiv className="absolute inset-0 flex items-center justify-center">
                    <DMButton
                      type="button"
                      className="flex h-[10vh] w-[10vh] items-center justify-center rounded-full bg-black/55 text-white"
                      aria-label={`Play ${item?.title || 'video'}`}
                    >
                      <Play
                        className="ml-[0.35vw] h-[5.4vh] w-[5.4vh] fill-white"
                        strokeWidth={1.5}
                      />
                    </DMButton>
                  </DMDiv>
                </>
              )}
            </DMDiv>
          </DMDiv>

          <DMDiv className="border-t border-gray-200 px-[1.4vw] py-[1.8vh]">
            <DMText
              as="h3"
              className="mb-[0.8vh] text-[1.55vh] font-bold uppercase leading-none text-gray-500"
            >
              Description
            </DMText>
            <DMText className="whitespace-pre-wrap text-[1.75vh] font-medium leading-relaxed text-gray-700">
              {item?.description ||
                item?.content ||
                'No description available.'}
            </DMText>
          </DMDiv>
        </DMDiv>
      </DMDiv>
    </DMDiv>
  );
};

const isImageUrl = (url: string) =>
  /\.(png|jpe?g|webp|gif|avif)(\?.*)?$/i.test(url || '');

function UploadVideosList() {
  const navigate = useNavigate();
  const authData = useAuth();
  const [selectedVideo, setSelectedVideo] = React.useState<VideoItem | null>(
    null
  );
  const [searchQuery, setSearchQuery] = React.useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 900);
  const [isLoadingVideos, setIsLoadingVideos] = React.useState(true);
  const [isLoadingVideoDetails, setIsLoadingVideoDetails] =
    React.useState(false);
  const [shareVideo, setShareVideo] = React.useState<VideoItem | null>(null);
  const [isPublishingVideo, setIsPublishingVideo] = React.useState(false);
  const [publishingPlatform, setPublishingPlatform] =
    React.useState<VideoPlatformKey | null>(null);
  const [videoRows, setVideoRows] = React.useState<VideoItem[]>([]);
  const [totalVideos, setTotalVideos] = React.useState(0);
  const [pageIndex, setPageIndex] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);
  const [platforms, setPlatforms] = React.useState(emptyPlatforms);

  const filteredVideos = React.useMemo(() => {
    const normalizedSearch = debouncedSearchQuery.trim().toLowerCase();

    if (!normalizedSearch) {
      return videoRows;
    }

    return videoRows.filter(
      item =>
        item.title.toLowerCase().includes(normalizedSearch) ||
        item.school.toLowerCase().includes(normalizedSearch) ||
        item.date.toLowerCase().includes(normalizedSearch) ||
        item.description.toLowerCase().includes(normalizedSearch)
    );
  }, [debouncedSearchQuery, videoRows]);

  const fetchVideos = React.useCallback(async () => {
    setIsLoadingVideos(true);

    try {
      const response = await httpclient().get(
        `/master/branch/listVideos?page_index=${pageIndex}&page_size=${pageSize}`
      );
      setVideoRows(getVideoList(response).map(mapVideoRow));
      setTotalVideos(getVideoTotal(response));
    } catch (error: any) {
      showGlobalModal(
        getApiErrorMessage(error, 'Unable to fetch videos'),
        'error'
      );
      setVideoRows([]);
      setTotalVideos(0);
    } finally {
      setIsLoadingVideos(false);
    }
  }, [pageIndex, pageSize]);

  React.useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  React.useEffect(() => {
    setPageIndex(1);
  }, [debouncedSearchQuery]);

  const handleOpenVideo = async (item: VideoItem) => {
    setSelectedVideo(item);

    if (!item.id) {
      return;
    }

    setIsLoadingVideoDetails(true);

    try {
      const response = await httpclient().get(
        `/master/branch/viewVideo/${item.id}`
      );
      const detail = response?.data?.data;
      if (detail) {
        setSelectedVideo(mapVideoRow(detail));
      }
    } catch (error: any) {
      showGlobalModal(
        getApiErrorMessage(error, 'Unable to fetch video details'),
        'error'
      );
    } finally {
      setIsLoadingVideoDetails(false);
    }
  };

  const handleOpenShareModal = (video: VideoItem) => {
    setPlatforms({
      facebook: video.facebook,
      instagram: video.instagram,
      youtube: video.youtube,
    });
    setShareVideo(video);
  };

  const publishVideoPlatform = async (platformKey: VideoPlatformKey) => {
    if (!shareVideo?.id) {
      showGlobalModal('Unable to publish this video', 'error');
      return;
    }

    if (!shareVideo.availablePlatforms.includes(platformKey)) {
      showGlobalModal(
        'This social platform is not available for this video',
        'warning'
      );
      return;
    }

    setIsPublishingVideo(true);
    setPublishingPlatform(platformKey);

    let fallbackErrorMessage = 'Unable to update video platform';

    try {
      const platform = platformKey.toUpperCase();

      if (
        platformKey === 'facebook' ||
        platformKey === 'instagram' ||
        platformKey === 'youtube'
      ) {
        const platformLabel = videoPlatformLabels[platformKey];

        if (platformKey === 'youtube' && !authData?.BRANCH_ID) {
          showGlobalModal('Branch id is missing', 'error');
          return;
        }

        const socialVideoPayload = {
          ...buildFacebookVideoPayload(shareVideo),
          ...(platformKey === 'youtube'
            ? { BRANCH_ID: authData?.BRANCH_ID as number | string }
            : {}),
        };

        if (!socialVideoPayload.MEDIA.length) {
          showGlobalModal('Video media is missing', 'error');
          return;
        }

        fallbackErrorMessage = `Unable to publish video on ${platformLabel}`;
        const socialPostResponse = await httpclient().post(
          `/comm/${platformKey}/post`,
          socialVideoPayload
        );
        openSocialPostUrl(socialPostResponse);
        fallbackErrorMessage = 'Unable to update video platform';
      }

      const response = await httpclient().post(
        `/master/branch/updateVideoPlatform/${shareVideo.id}`,
        { platform }
      );
      const updatedVideo = response?.data?.data || {};

      showGlobalModal(
        response?.data?.message ||
          `Video published on ${videoPlatformLabels[platformKey]} successfully`
      );
      setPlatforms(current => ({ ...current, [platformKey]: true }));
      setShareVideo(current =>
        current
          ? {
              ...current,
              facebook: Boolean(
                updatedVideo.facebook ??
                  (platformKey === 'facebook' ? true : current.facebook)
              ),
              instagram: Boolean(
                updatedVideo.instagram ??
                  (platformKey === 'instagram' ? true : current.instagram)
              ),
              youtube: Boolean(
                updatedVideo.youtube ??
                  (platformKey === 'youtube' ? true : current.youtube)
              ),
            }
          : current
      );
      setVideoRows(current =>
        current.map(item =>
          item.id === shareVideo.id
            ? {
                ...item,
                facebook: Boolean(
                  updatedVideo.facebook ??
                    (platformKey === 'facebook' ? true : item.facebook)
                ),
                instagram: Boolean(
                  updatedVideo.instagram ??
                    (platformKey === 'instagram' ? true : item.instagram)
                ),
                youtube: Boolean(
                  updatedVideo.youtube ??
                    (platformKey === 'youtube' ? true : item.youtube)
                ),
              }
            : item
        )
      );
    } catch (error: any) {
      showGlobalModal(getApiErrorMessage(error, fallbackErrorMessage), 'error');
    } finally {
      setIsPublishingVideo(false);
      setPublishingPlatform(null);
    }
  };

  const requestPublishPlatform = (platformKey: VideoPlatformKey) => {
    if (platforms[platformKey]) {
      return;
    }

    showGlobalModal(
      `Are you sure you want to publish this video on ${videoPlatformLabels[platformKey]}? Once published, this platform cannot be changed from here.`,
      'warning',
      {
        showConfirm: true,
        onConfirm: () => publishVideoPlatform(platformKey),
        onCancel: () => undefined,
        confimLabel: 'Publish',
        cancelLabel: 'Cancel',
      }
    );
  };

  return (
    <DMDiv
      as="main"
      className="flex h-full min-h-0 flex-col overflow-hidden bg-white px-[2.5vw] py-[3.2vh] font-inter text-gray-950"
    >
      <DigitalMarketingBreadcrumb
        className="mb-[2.2vh] shrink-0"
        items={[{ label: 'Upload Videos' }]}
      />

      <DMDiv className="mb-[3.8vh] flex shrink-0 items-start justify-between gap-[2vw]">
        <DMDiv>
          <DMText
            as="h1"
            className="text-[3.5vh] font-bold leading-tight text-gray-950"
          >
            Videos
          </DMText>
          <DMText className="mt-[0.65vh] text-[1.65vh] font-medium leading-tight text-gray-600">
            Collection of videos uploaded in the past
          </DMText>
        </DMDiv>

        <DMDiv className="flex shrink-0 items-center gap-[0.8vw]">
          <DigitalMarketingSearchInput
            value={searchQuery}
            onChange={setSearchQuery}
          />
          <DMButton
            type="button"
            onClick={() => navigate(buildUploadVideosPath('create'))}
            className="flex h-[6.4vh] items-center gap-[0.8vw] rounded-full bg-primary-main px-[2vw] text-[1.7vh] font-semibold text-white shadow-sm transition-opacity hover:opacity-90"
          >
            <Plus className="h-[2.4vh] w-[2.4vh]" strokeWidth={2.1} />
            <DMText as="span">Upload Video</DMText>
          </DMButton>
        </DMDiv>
      </DMDiv>

      <PaginatedCardGrid
        emptyMessage="No videos found."
        hasItems={filteredVideos.length > 0}
        isLoading={isLoadingVideos}
        itemLabel="video"
        itemLabelPlural="videos"
        pageIndex={pageIndex}
        pageSize={pageSize}
        pageSizeOptions={[10, 20, 30, 40, 50]}
        totalItems={totalVideos}
        onPageIndexChange={setPageIndex}
        onPageSizeChange={setPageSize}
        renderSkeleton={index => <VideoCardSkeleton key={index} />}
      >
        {filteredVideos.map(item => (
          <VideoCard
            key={`${item.id}-${item.title}`}
            item={item}
            onPlay={handleOpenVideo}
            onShare={handleOpenShareModal}
          />
        ))}
      </PaginatedCardGrid>

      <VideoPlayerModal
        item={selectedVideo}
        isLoading={isLoadingVideoDetails}
        onClose={() => {
          setSelectedVideo(null);
          setIsLoadingVideoDetails(false);
        }}
      />
      <VideoPublishModal
        isOpen={!!shareVideo}
        onClose={() => {
          if (isPublishingVideo) {
            return;
          }

          setShareVideo(null);
          setPlatforms(emptyPlatforms);
        }}
        platforms={platforms}
        visiblePlatforms={shareVideo?.availablePlatforms || []}
        requestPublish={requestPublishPlatform}
        isPublishing={isPublishingVideo}
        publishingPlatform={publishingPlatform}
      />
    </DMDiv>
  );
}

function UploadVideoForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const authData = useAuth();
  const [videoForm, setVideoForm] = React.useState<VideoFormValues>(
    () =>
      location.state?.videoForm || {
        ...emptyVideoForm,
      }
  );
  const [isUploadingVideo, setIsUploadingVideo] = React.useState(false);
  const [isUploadingMusic, setIsUploadingMusic] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isGeneratingDescription, setIsGeneratingDescription] =
    React.useState(false);
  const [descriptionGenerationCount, setDescriptionGenerationCount] =
    React.useState(0);
  const descriptionGenerationsRemaining =
    maxDescriptionGenerations - descriptionGenerationCount;
  const descriptionGenerateButtonLabel =
    videoForm.description.trim() || descriptionGenerationCount > 0
      ? 'Re-generate'
      : 'Generate';

  const handleUploadFile = async (file: File, kind: 'video' | 'music') => {
    const isVideo = kind === 'video';

    if (isVideo && !allowedVideoTypes.includes(file.type)) {
      showGlobalModal(
        'Only MP4, WEBM, and MOV video files are allowed',
        'error'
      );
      return;
    }

    if (!isVideo && !file.type.startsWith('audio/')) {
      showGlobalModal(
        'Only audio files are allowed for background music',
        'error'
      );
      return;
    }

    if (isVideo && file.size > maxVideoSize) {
      showGlobalModal('Video must be 100MB or less', 'error');
      return;
    }

    if (isVideo) {
      setIsUploadingVideo(true);
    } else {
      setIsUploadingMusic(true);
    }

    try {
      const formData = new FormData();
      formData.append('dest', 'DM-CONTENT');
      formData.append('branch_id', authData?.BRANCH_ID as any);
      formData.append('database', authData?.DATABASE as any);
      formData.append('files', file);
      const response = await apiFileUpload(
        formData,
        'upload',
        '',
        '',
        true,
        false,
        true
      );
      const fileUrl = extractUploadedUrl(response);

      if (!fileUrl) {
        showGlobalModal('Unable to read uploaded file URL', 'error');
        return;
      }

      setVideoForm(current => ({
        ...current,
        ...(isVideo
          ? { videoUrl: fileUrl, videoFileName: file.name }
          : {
              backgroundMusicUrl: fileUrl,
              backgroundMusicFileName: file.name,
            }),
      }));
    } catch (error: any) {
      showGlobalModal(
        getApiErrorMessage(error, 'Failed to upload file'),
        'error'
      );
    } finally {
      if (isVideo) {
        setIsUploadingVideo(false);
      } else {
        setIsUploadingMusic(false);
      }
    }
  };

  const handleUploadVideo = async () => {
    if (!videoForm.title || !videoForm.description || !videoForm.videoUrl) {
      showGlobalModal('Complete required details before uploading', 'warning');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await httpclient().post('/master/branch/uploadVideo', {
        title: videoForm.title,
        description: videoForm.description,
        content: videoForm.content,
        video_url: videoForm.videoUrl,
        background_music_url: videoForm.backgroundMusicUrl,
        presentation_style: videoForm.presentationStyle,
        platform: videoForm.platform || [],
      });
      showGlobalModal(response?.data?.message || 'Uploaded successfully');
      navigate(UPLOAD_VIDEOS_BASE_PATH);
    } catch (error: any) {
      showGlobalModal(getApiErrorMessage(error, 'Unable to upload'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGenerateDescription = async () => {
    if (descriptionGenerationsRemaining <= 0) {
      showGlobalModal('Description generation limit reached.', 'warning');
      return;
    }

    if (!videoForm.title.trim()) {
      showGlobalModal(
        'Complete title before generating a description.',
        'warning'
      );
      return;
    }

    setIsGeneratingDescription(true);

    try {
      const description = await dmTextGenerationApi.generateDescription({
        type: 'CONTENT',
        title: videoForm.title,
        category_type: 'VIDEO',
      });

      if (description) {
        setVideoForm(current => ({ ...current, description }));
        setDescriptionGenerationCount(current =>
          Math.min(current + 1, maxDescriptionGenerations)
        );
      } else {
        showGlobalModal('Unable to generate description', 'error');
      }
    } catch (error: any) {
      showGlobalModal(
        getApiErrorMessage(error, 'Unable to generate description'),
        'error'
      );
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  const handleRemoveVideo = () => {
    setVideoForm(current => ({
      ...current,
      videoUrl: '',
      videoFileName: '',
    }));
  };

  return (
    <DMDiv
      as="main"
      className="flex h-full min-h-0 flex-col overflow-hidden bg-white px-[2.5vw] py-[3.2vh] font-inter text-gray-950"
    >
      <DMDiv className="mb-[4.5vh] shrink-0">
        <DigitalMarketingBreadcrumb
          className="mb-[1.9vh]"
          items={[
            { label: 'Upload Videos', path: UPLOAD_VIDEOS_BASE_PATH },
            { label: 'Upload Video' },
          ]}
        />
        <DMText
          as="h1"
          className="text-[3.8vh] font-bold leading-none text-gray-950"
        >
          Upload Video
        </DMText>
        <DMText className="mt-[1vh] text-[1.8vh] font-medium leading-tight text-gray-600">
          Enter a title and hashtags, then upload a single video file.
        </DMText>
      </DMDiv>

      <DMDiv as="form" className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <DMDiv className="min-h-0 flex-1 overflow-y-auto pr-[0.4vw]">
          <DMDiv>
            <DMLabel className={labelClassName}>Title</DMLabel>
            <DMInput
              className={inputClassName}
              placeholder="Enter Title"
              value={videoForm.title}
              onChange={event =>
                setVideoForm(current => ({
                  ...current,
                  title: event.target.value,
                }))
              }
            />
          </DMDiv>

          <DMDiv className="mt-[1.6vh]">
            <DMLabel className={labelClassName}>Hashtags</DMLabel>
            <DMDiv className="flex h-[17.8vh] flex-col rounded-[10px] border border-gray-300 bg-white px-[1.1vw] py-[1.4vh] focus-within:border-primary-main">
              <DMTextarea
                className="min-h-0 flex-1 resize-none bg-transparent text-[1.85vh] font-medium text-gray-950 outline-none placeholder:text-gray-400"
                placeholder="Enter hashtags"
                value={videoForm.description}
                onChange={event =>
                  setVideoForm(current => ({
                    ...current,
                    description: event.target.value,
                  }))
                }
              />
              <DMButton
                type="button"
                onClick={handleGenerateDescription}
                disabled={
                  isGeneratingDescription ||
                  descriptionGenerationsRemaining <= 0
                }
                className="mx-auto mt-[1vh] flex h-[5.5vh] w-[19.8vw] items-center justify-center gap-[0.8vw] rounded-full bg-primary-main text-[1.75vh] font-semibold text-white disabled:opacity-60"
              >
                <Sparkles className="h-[2.1vh] w-[2.1vh]" strokeWidth={2} />
                {isGeneratingDescription
                  ? 'Generating...'
                  : descriptionGenerateButtonLabel}
              </DMButton>
            </DMDiv>
          </DMDiv>

          <DMDiv className="mt-[1.6vh]">
            <DMLabel className={labelClassName}>Content</DMLabel>
            <DMTextarea
              className="h-[11.2vh] w-full resize-none rounded-[10px] border border-gray-300 bg-white px-[1.1vw] py-[1.7vh] text-[1.85vh] font-medium text-gray-950 outline-none placeholder:text-gray-400 focus:border-primary-main"
              placeholder="Enter post content"
              value={videoForm.content}
              onChange={event =>
                setVideoForm(current => ({
                  ...current,
                  content: event.target.value,
                }))
              }
            />
          </DMDiv>

          <DMDiv className="mt-[1.6vh]">
            <DMLabel className={labelClassName}>Upload Video</DMLabel>
            <DMDiv className="flex min-h-[25.4vh] flex-col items-center justify-center rounded-[10px] border border-dashed border-gray-300 bg-white px-[1.4vw] py-[2.4vh] text-center">
              {videoForm.videoUrl ? (
                <DMDiv className="group relative aspect-video w-full max-w-[26vw] overflow-hidden rounded-[8px] bg-gray-100">
                  <DMVideo
                    src={videoForm.videoUrl}
                    className="h-full w-full object-cover"
                    controls
                  />
                  <DMButton
                    type="button"
                    onClick={handleRemoveVideo}
                    className="absolute right-[0.6vh] top-[0.6vh] flex h-[3.4vh] w-[3.4vh] items-center justify-center rounded-full bg-white text-gray-800 shadow hover:bg-gray-100"
                    aria-label="Remove uploaded video"
                  >
                    <X className="h-[2vh] w-[2vh]" strokeWidth={2.4} />
                  </DMButton>
                </DMDiv>
              ) : (
                <>
                  <Video
                    className="h-[5vh] w-[5vh] text-primary-main"
                    strokeWidth={1.7}
                  />
                  <DMText className="mt-[1.4vh] text-[1.75vh] font-bold leading-tight text-gray-950">
                    Choose a video file or drag &amp; drop it here
                  </DMText>
                  <DMText className="mt-[1.1vh] text-[1.55vh] font-medium leading-tight text-gray-500">
                    MP4, WEBM, MOV (Max 100mb)
                  </DMText>
                </>
              )}
              <DMLabel className="mt-[1.4vh] flex h-[6.4vh] w-[17.5vw] cursor-pointer items-center justify-center gap-[0.8vw] rounded-full border border-primary-main bg-white text-[1.85vh] font-semibold text-primary-main">
                <UploadCloud className="h-[2.1vh] w-[2.1vh]" strokeWidth={2} />
                <DMText as="span">
                  {isUploadingVideo
                    ? 'Uploading...'
                    : videoForm.videoUrl
                    ? 'Change Video'
                    : 'Upload Video'}
                </DMText>
                <DMInput
                  type="file"
                  accept="video/mp4,video/webm,video/quicktime"
                  className="hidden"
                  disabled={isUploadingVideo}
                  onChange={event => {
                    const file = event.target.files?.[0];
                    if (file) {
                      handleUploadFile(file, 'video');
                    }
                    event.currentTarget.value = '';
                  }}
                />
              </DMLabel>
              {videoForm.videoFileName && (
                <DMText className="mt-[1vh] max-w-[40vw] truncate text-[1.35vh] font-medium text-gray-500">
                  {videoForm.videoFileName}
                </DMText>
              )}
            </DMDiv>
          </DMDiv>
        </DMDiv>

        <DMDiv className="mt-auto flex shrink-0 justify-end pt-[3vh]">
          <DMButton
            type="button"
            onClick={handleUploadVideo}
            disabled={
              !videoForm.title ||
              !videoForm.description ||
              !videoForm.videoUrl ||
              isUploadingVideo ||
              isUploadingMusic ||
              isSubmitting
            }
            className="flex h-[6.6vh] w-[21vw] items-center justify-center rounded-full bg-primary-main text-[1.85vh] font-semibold text-white disabled:opacity-60"
          >
            {isSubmitting ? <SetupLoader height="h-5" /> : 'Upload'}
          </DMButton>
        </DMDiv>
      </DMDiv>
    </DMDiv>
  );
}

function UploadVideoPreview() {
  const navigate = useNavigate();
  const location = useLocation();
  const videoForm: VideoFormValues =
    location.state?.videoForm || emptyVideoForm;
  const title = videoForm.title || 'Untitled Video';
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleUploadVideo = async () => {
    if (!videoForm.title || !videoForm.description || !videoForm.videoUrl) {
      showGlobalModal('Complete required details before uploading', 'warning');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await httpclient().post('/master/branch/uploadVideo', {
        title: videoForm.title,
        description: videoForm.description,
        content: videoForm.content,
        video_url: videoForm.videoUrl,
        background_music_url: videoForm.backgroundMusicUrl,
        presentation_style: videoForm.presentationStyle,
        platform: videoForm.platform || [],
      });
      showGlobalModal(response?.data?.message || 'Uploaded successfully');
      navigate(UPLOAD_VIDEOS_BASE_PATH);
    } catch (error: any) {
      showGlobalModal(getApiErrorMessage(error, 'Unable to upload'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DMDiv
      as="main"
      className="min-h-full bg-white px-[2.5vw] py-[3.2vh] pb-[6vh] font-inter text-gray-950"
    >
      <DMButton
        type="button"
        onClick={() =>
          navigate(buildUploadVideosPath('create'), { state: { videoForm } })
        }
        className="mb-6 flex shrink-0 items-center gap-2 text-sm font-medium text-[#5E4DE0] hover:underline"
      >
        <ChevronLeft size={16} /> Back to Edit Video
      </DMButton>

      <DMDiv className="mb-8">
        <DMText as="h1" className="text-3xl font-bold text-gray-900">
          Preview Video
        </DMText>
        <DMText className="text-gray-500">
          This is how your video will look before uploading.
        </DMText>
      </DMDiv>

      <DMDiv>
        <DMDiv className="mx-auto max-w-7xl rounded-2xl bg-white p-6 shadow-sm">
          <DMButton
            type="button"
            className="relative mb-8 block aspect-[21/9] w-full overflow-hidden rounded-xl bg-gray-100 text-left"
          >
            {videoForm.videoUrl && isImageUrl(videoForm.videoUrl) ? (
              <DMImage
                src={videoForm.videoUrl}
                alt={title}
                className="h-full w-full object-cover"
              />
            ) : videoForm.videoUrl ? (
              <DMVideo
                src={videoForm.videoUrl}
                className="h-full w-full object-cover"
                controls
              />
            ) : (
              <>
                <DMImage
                  src={imageBg}
                  alt={title}
                  className="h-full w-full object-cover"
                />
                <DMDiv className="absolute inset-0 flex items-center justify-center">
                  <DMText
                    as="span"
                    className="flex h-[8.5vh] w-[8.5vh] items-center justify-center rounded-full bg-black/70 text-white"
                  >
                    <Play
                      className="ml-[0.3vw] h-[4.6vh] w-[4.6vh] fill-white"
                      strokeWidth={1.6}
                    />
                  </DMText>
                </DMDiv>
              </>
            )}
          </DMButton>

          <DMText
            as="h1"
            className="mb-4 text-3xl font-bold leading-tight text-gray-900"
          >
            {title}
          </DMText>

          <DMDiv className="mb-8 text-sm text-gray-500">
            {new Date().toLocaleDateString()} | DemoSchool
          </DMDiv>

          <DMDiv className="prose max-w-none whitespace-pre-wrap leading-relaxed text-gray-700">
            {videoForm.content || videoForm.description}
          </DMDiv>
        </DMDiv>
      </DMDiv>

      <DMDiv className="mx-auto mt-4 flex w-full max-w-7xl justify-end gap-4">
        <DMButton
          type="button"
          onClick={handleUploadVideo}
          disabled={isSubmitting}
          className="flex min-w-[150px] items-center justify-center rounded-full bg-[#5E4DE0] px-10 py-3 font-medium text-white transition-colors hover:bg-[#4638C0] disabled:opacity-60"
        >
          {isSubmitting ? <SetupLoader height="h-5" /> : 'Upload Video'}
        </DMButton>
      </DMDiv>
    </DMDiv>
  );
}

export function UploadVideos() {
  const location = useLocation();
  const isCreate = location.pathname.endsWith('/create');
  const isPreview = location.pathname.endsWith('/preview');

  if (isPreview) {
    return <UploadVideoPreview />;
  }

  return isCreate ? <UploadVideoForm /> : <UploadVideosList />;
}
